<template>
  <div class="rights-record-manager">
    <div class="manager-intro">
      <div>
        <h3>版权档案库</h3>
        <p>合同、平台许可、CC/CC0 来源和证明文件在这里维护一次，再关联给一个或多个资源。</p>
      </div>
      <el-button type="primary" icon="el-icon-plus" @click="createRecord">
        新建版权档案
      </el-button>
    </div>

    <div class="manager-filters">
      <el-input
        v-model.trim="filters.keyword"
        clearable
        prefix-icon="el-icon-search"
        placeholder="搜索档案名称、编号或权利人"
        @keyup.enter.native="loadList()"
        @clear="loadList()" />
      <el-select
        v-model="filters.record_type"
        clearable
        placeholder="档案类型"
        @change="loadList()">
        <el-option
          v-for="item in options.record_types"
          :key="item.value"
          :label="item.label"
          :value="item.value" />
      </el-select>
      <el-select
        v-model="filters.verification_status"
        clearable
        placeholder="审核进度"
        @change="loadList()">
        <el-option
          v-for="item in options.verification_statuses"
          :key="item.value"
          :label="item.label"
          :value="item.value" />
      </el-select>
      <el-button type="primary" icon="el-icon-search" @click="loadList()">查询</el-button>
      <el-button @click="resetFilters">重置</el-button>
    </div>

    <el-table v-loading="loading" :data="list" style="width:100%">
      <el-table-column label="档案" min-width="240">
        <template slot-scope="scope">
          <div class="record-name">{{ scope.row.record_name }}</div>
          <div class="record-code">{{ scope.row.record_code }}</div>
        </template>
      </el-table-column>
      <el-table-column label="类型" width="150">
        <template slot-scope="scope">
          {{ optionLabel(options.record_types, scope.row.record_type, '其他依据') }}
        </template>
      </el-table-column>
      <el-table-column label="版权情况" min-width="150">
        <template slot-scope="scope">
          {{ optionLabel(options.rights_statuses, scope.row.rights_status, '未知') }}
        </template>
      </el-table-column>
      <el-table-column label="审核" width="110">
        <template slot-scope="scope">
          <el-tag size="small" :type="verificationType(scope.row.verification_status)">
            {{ optionLabel(options.verification_statuses, scope.row.verification_status, '未审核') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="使用范围" width="130">
        <template slot-scope="scope">
          <el-tag size="small" effect="plain">
            {{ optionLabel(options.usage_modes, scope.row.usage_mode, '可复用') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="关联资源" width="100" align="center">
        <template slot-scope="scope">{{ Number(scope.row.sound_count || 0) }}</template>
      </el-table-column>
      <el-table-column label="证明文件" width="100" align="center">
        <template slot-scope="scope">{{ Number(scope.row.evidence_count || 0) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="100" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" icon="el-icon-edit" @click="editRecord(scope.row)">编辑</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="manager-pagination"
      background
      :current-page="page.page_num"
      :page-size="page.page_size"
      :page-sizes="[10, 20, 50, 100]"
      :total="page.total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handlePageSizeChange"
      @current-change="handlePageChange" />

    <SoundRightsRecordDialog
      :visible.sync="dialogVisible"
      :record-id="currentRecordId"
      @saved="handleRecordSaved" />
  </div>
</template>

<script>
import apis from '@/api/index'
import SoundRightsRecordDialog from '@/components/sounds/SoundRightsRecordDialog.vue'
import { optionLabel, verificationTagType } from '@/lib/soundAdminUi'

export default {
  name: 'SoundRightsRecordManager',
  components: {
    SoundRightsRecordDialog
  },
  /**
   * 创建档案库的筛选、分页和弹窗状态。
   *
   * @returns {Object}
   */
  data() {
    return {
      loading: false,
      dialogVisible: false,
      currentRecordId: null,
      list: [],
      options: {
        usage_modes: [],
        record_types: [],
        rights_statuses: [],
        verification_statuses: []
      },
      filters: {
        keyword: '',
        record_type: '',
        verification_status: ''
      },
      page: {
        page_num: 1,
        page_size: 20,
        total: 0
      }
    }
  },
  /**
   * 首次显示组件时加载统一选项和第一页档案。
   *
   * @returns {Promise<void>}
   */
  async created() {
    await this.loadOptions()
    await this.loadList(false)
  },
  methods: {
    optionLabel,
    verificationType: verificationTagType,
    /**
     * 加载档案类型、版权情况和审核进度等统一选项。
     *
     * @returns {Promise<void>}
     */
    async loadOptions() {
      try {
        const response = await apis.soundRightsRecord.options()
        if (response.data.code !== 1) throw new Error(response.data.msg || '版权档案选项加载失败')
        this.options = { ...this.options, ...(response.data.data || {}) }
      } catch (error) {
        this.$message.error(error.message || String(error))
      }
    },

    /**
     * 按当前筛选和分页读取版权档案。
     *
     * @param {Boolean} resetPage 是否返回第一页
     * @returns {Promise<void>}
     */
    async loadList(resetPage = true) {
      if (resetPage) this.page.page_num = 1
      this.loading = true
      try {
        const response = await apis.soundRightsRecord.lists({
          ...this.filters,
          page_num: this.page.page_num,
          page_size: this.page.page_size
        })
        if (response.data.code !== 1) throw new Error(response.data.msg || '版权档案加载失败')
        const data = response.data.data || {}
        this.list = data.list || []
        this.page.total = Number(data.count || 0)
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.loading = false
      }
    },

    /**
     * 清空筛选并重新加载第一页。
     *
     * @returns {void}
     */
    resetFilters() {
      this.filters = {
        keyword: '',
        record_type: '',
        verification_status: ''
      }
      this.loadList()
    },

    /**
     * 打开新建版权档案弹窗。
     *
     * @returns {void}
     */
    createRecord() {
      this.currentRecordId = null
      this.dialogVisible = true
    },

    /**
     * 打开指定版权档案的编辑弹窗。
     *
     * @param {Object} record 当前列表行
     * @returns {void}
     */
    editRecord(record) {
      this.currentRecordId = Number(record.id)
      this.dialogVisible = true
    },

    /**
     * 档案保存后刷新列表并通知外层工作台。
     *
     * @param {Object} record 保存后的档案
     * @returns {void}
     */
    handleRecordSaved(record) {
      this.loadList(false)
      this.$emit('record-saved', record)
    },

    /**
     * 修改每页数量并返回第一页。
     *
     * @param {Number} size 每页数量
     * @returns {void}
     */
    handlePageSizeChange(size) {
      this.page.page_size = size
      this.loadList()
    },

    /**
     * 切换当前页。
     *
     * @param {Number} pageNum 页码
     * @returns {void}
     */
    handlePageChange(pageNum) {
      this.page.page_num = pageNum
      this.loadList(false)
    }
  }
}
</script>

<style lang="scss" scoped>
.rights-record-manager {
  padding: 4px 2px 18px;
}

.manager-intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  h3 {
    margin: 0;
    color: #303133;
    font-size: 20px;
  }

  p {
    margin: 7px 0 0;
    color: #909399;
    line-height: 1.55;
  }
}

.manager-filters {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) 180px 160px auto auto;
  gap: 10px;
  margin: 18px 0;
}

.record-name {
  color: #303133;
  font-weight: 600;
}

.record-code {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.manager-pagination {
  margin-top: 18px;
  text-align: right;
}

@media (max-width: 1100px) {
  .manager-filters {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
